import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  PRODUCTION_FORMAT_LABELS,
  ProductionFormat,
} from '@scoutaround/script-breakdown-shared';

import { api, ApiError } from '../lib/api.js';
import { Callout } from '../components/Callout.js';
import { ScriptPrivacyNotice } from '../components/ScriptPrivacyNotice.js';

type Mode = 'UPLOAD' | 'PASTE' | 'MANUAL';
type Target = 'EXISTING' | 'NEW';

/**
 * WHERE A LOCATION SEARCH STARTS.
 *
 * Until this page existed the only way in was to create a project, open it,
 * find the Script & Scenes tab and upload there — four steps before the product
 * did anything, and the one a producer arrives wanting to do was the last of
 * them. This is the same three verbs the workspace already has, in the order
 * somebody actually has them in mind: here is my material, put it in this
 * production, go.
 *
 * A SCRIPT IS NOT REQUIRED. "Add requirements manually" is a first-class third
 * mode, not a fallback — a commercial or a music video routinely has a brief
 * and a shot list and no screenplay at all, and a page that assumes a
 * screenplay turns them away at the door.
 *
 * NOTHING IS PROMISED THAT IS NOT TRUE. The notice below says what this service
 * actually does with a screenplay, which is not the same as what would be
 * reassuring to say — see the comment on `ScriptPrivacyNotice`.
 */
export function NewSearchPage({
  onOpen,
  onBack,
}: {
  onOpen: (projectId: string, tabSlug: string) => void;
  onBack: () => void;
}) {
  const [mode, setMode] = useState<Mode>('UPLOAD');
  const [file, setFile] = useState<File | null>(null);
  const [pasted, setPasted] = useState('');
  const [target, setTarget] = useState<Target>('EXISTING');
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newFormat, setNewFormat] = useState<ProductionFormat>('FEATURE_FILM');
  const [error, setError] = useState<string | null>(null);

  const projects = useQuery({ queryKey: ['projects'], queryFn: () => api.listProjects() });

  const visible = useMemo(() => {
    const term = filter.trim().toLowerCase();
    const all = projects.data ?? [];
    return term === '' ? all : all.filter((p) => p.title.toLowerCase().includes(term));
  }, [projects.data, filter]);

  const start = useMutation({
    mutationFn: async () => {
      // ONE PROJECT, WHICHEVER WAY IN. Creating it first means every later step
      // has somewhere to belong, and a failed upload leaves a real, openable
      // production rather than an orphaned file.
      const projectId =
        target === 'NEW'
          ? (
              await api.createProject({
                title: newTitle.trim(),
                format: newFormat,
                productionModel: 'TRADITIONAL',
              })
            ).id
          : (selected ?? '');

      if (projectId === '') throw new ApiError(400, null, 'Pick a project first');

      if (mode === 'MANUAL') return { projectId, tab: 'locations' };

      const version =
        mode === 'UPLOAD' && file !== null
          ? await api.uploadScript(projectId, file)
          : await api.pasteScript(projectId, pasted);

      // The analysis itself. It is a background job — the pipeline run id comes
      // back immediately and the Script tab shows the run's own progress.
      await api.requestExtraction(projectId, version.scriptVersionId);

      return { projectId, tab: 'script' };
    },
    onSuccess: ({ projectId, tab }) => {
      onOpen(projectId, tab);
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'That could not be started.');
    },
  });

  const ready =
    (target === 'EXISTING' ? selected !== null : newTitle.trim() !== '') &&
    (mode === 'MANUAL' ||
      (mode === 'UPLOAD' ? file !== null : pasted.trim().length > 0));

  return (
    <div
      data-testid="new-search-page"
      style={{
        // THE PAGE'S OWN GUTTER. Every other screen carries one — the project
        // list, the workspace shell — because this app has no outer container;
        // without it the content sat flush against the left edge of the window,
        // which reads as a broken page rather than a missing rule. `clamp` so a
        // phone gets 18px instead of a desktop's 40 and the form is not a
        // column down the middle of a narrow screen.
        maxWidth: 1360,
        margin: '0 auto',
        padding: 'clamp(22px, 3vw, 34px) clamp(18px, 4vw, 40px) 64px',
      }}
    >
      <nav className="mm" style={{ fontSize: 12.5, marginBottom: 18 }} aria-label="Breadcrumb">
        <button
          type="button"
          data-testid="new-search-back"
          onClick={onBack}
          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit', textDecoration: 'underline' }}
        >
          Projects
        </button>
        <span aria-hidden="true"> › </span>
        <span style={{ fontWeight: 700, color: 'var(--ink)' }}>New location search</span>
      </nav>

      <p
        className="flbl"
        style={{ color: 'var(--color-accent)', letterSpacing: '0.1em', margin: 0 }}
      >
        AI script breakdown
      </p>
      <h1 className="dh" style={{ fontSize: 'clamp(28px, 5vw, 46px)', margin: '10px 0 0' }}>
        Find locations for your production
      </h1>
      <p className="mm" style={{ fontSize: 14, margin: '12px 0 0', maxWidth: '62ch', lineHeight: 1.6 }}>
        Upload a screenplay, paste a creative brief, or describe what you need — we break it into
        scenes and location requirements, then match real locations.
      </p>

      <div className="split-2" style={{ gap: 24, marginTop: 26, alignItems: 'start' }}>
        <div style={{ minWidth: 0 }}>
          <div
            role="radiogroup"
            aria-label="How you want to start"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}
          >
            {MODES.map((m) => (
              <button
                key={m.mode}
                type="button"
                role="radio"
                aria-checked={mode === m.mode}
                data-testid={`search-mode-${m.mode}`}
                onClick={() => {
                  setMode(m.mode);
                }}
                style={{
                  textAlign: 'left',
                  padding: 16,
                  cursor: 'pointer',
                  background: 'var(--color-bg)',
                  border:
                    mode === m.mode
                      ? '2px solid var(--ink)'
                      : '1px solid var(--color-divider)',
                }}
              >
                <span style={{ display: 'block', fontSize: 14, fontWeight: 700 }}>{m.title}</span>
                <span className="mm" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
                  {m.hint}
                </span>
              </button>
            ))}
          </div>

          {mode === 'UPLOAD' ? (
            <FilePicker file={file} onPick={setFile} />
          ) : mode === 'PASTE' ? (
            <label style={{ display: 'block', marginTop: 16 }}>
              <span className="flbl">Screenplay, treatment or brief</span>
              <textarea
                data-testid="search-paste"
                value={pasted}
                rows={12}
                placeholder="Paste the pages, a treatment, or a shot list…"
                onChange={(e) => {
                  setPasted(e.target.value);
                }}
                style={{ width: '100%', marginTop: 6, padding: 12, fontSize: 13, border: '1px solid var(--color-divider)', fontFamily: 'inherit' }}
              />
            </label>
          ) : (
            <p
              className="mm"
              data-testid="search-manual-note"
              style={{ fontSize: 13, marginTop: 16, lineHeight: 1.6, maxWidth: '58ch' }}
            >
              We will open the production with an empty location list. Add each requirement — what
              the place has to be, where, and for which scenes — and matching runs against them the
              same way it would against a screenplay.
            </p>
          )}

          <ScriptPrivacyNotice />
        </div>

        <aside
          data-testid="search-target"
          style={{ border: '1px solid var(--color-divider)', padding: 18, minWidth: 0 }}
        >
          <p className="flbl" style={{ margin: 0, letterSpacing: '0.08em' }}>
            Save this search to
          </p>

          <label
            data-testid="target-existing"
            style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, padding: 12, cursor: 'pointer', border: target === 'EXISTING' ? '2px solid var(--ink)' : '1px solid var(--color-divider)' }}
          >
            <input
              type="radio"
              name="search-target"
              checked={target === 'EXISTING'}
              onChange={() => {
                setTarget('EXISTING');
              }}
            />
            <span style={{ fontSize: 13.5, fontWeight: 700 }}>Existing project</span>
          </label>

          {target === 'EXISTING' ? (
            <>
              <input
                type="search"
                data-testid="project-filter"
                value={filter}
                placeholder="Search projects"
                onChange={(e) => {
                  setFilter(e.target.value);
                }}
                style={{ width: '100%', height: 38, marginTop: 10, padding: '0 12px', fontSize: 13, border: '1px solid var(--color-divider)' }}
              />

              {projects.isPending ? (
                <p className="mm" style={{ fontSize: 12.5, marginTop: 12 }} data-testid="projects-loading">
                  Loading your productions…
                </p>
              ) : visible.length === 0 ? (
                <p className="mm" style={{ fontSize: 12.5, marginTop: 12 }} data-testid="projects-empty">
                  {(projects.data ?? []).length === 0
                    ? 'You have no productions yet — start a new one below.'
                    : 'No production matches that.'}
                </p>
              ) : (
                <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, maxHeight: 260, overflowY: 'auto' }}>
                  {visible.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        data-testid={`project-${p.id}`}
                        aria-pressed={selected === p.id}
                        onClick={() => {
                          setSelected(p.id);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px 14px',
                          cursor: 'pointer',
                          background: selected === p.id ? 'var(--color-surface)' : 'transparent',
                          border: 0,
                          borderBottom: '1px solid var(--color-divider)',
                          display: 'flex',
                          gap: 10,
                          alignItems: 'baseline',
                        }}
                      >
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700 }}>
                            {p.title}
                          </span>
                          <span className="mm" style={{ fontSize: 11.5 }}>
                            {PRODUCTION_FORMAT_LABELS[p.format] ?? p.format} ·{' '}
                            {p.lifecyclePhase.toLowerCase().replace(/_/g, ' ')}
                          </span>
                        </span>
                        {selected === p.id ? (
                          <span aria-hidden="true" style={{ marginLeft: 'auto', fontWeight: 700 }}>
                            ✓
                          </span>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}

          <label
            data-testid="target-new"
            style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12, padding: 12, cursor: 'pointer', border: target === 'NEW' ? '2px solid var(--ink)' : '1px solid var(--color-divider)' }}
          >
            <input
              type="radio"
              name="search-target"
              checked={target === 'NEW'}
              onChange={() => {
                setTarget('NEW');
              }}
            />
            <span style={{ fontSize: 13.5, fontWeight: 700 }}>New project</span>
          </label>

          {target === 'NEW' ? (
            <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
              <label>
                <span className="flbl">Title</span>
                <input
                  data-testid="new-project-title"
                  value={newTitle}
                  placeholder="Northern Light"
                  onChange={(e) => {
                    setNewTitle(e.target.value);
                  }}
                  style={{ width: '100%', height: 38, marginTop: 6, padding: '0 12px', fontSize: 13, border: '1px solid var(--color-divider)' }}
                />
              </label>
              <label>
                <span className="flbl">Format</span>
                <select
                  data-testid="new-project-format"
                  value={newFormat}
                  onChange={(e) => {
                    setNewFormat(e.target.value as ProductionFormat);
                  }}
                  style={{ width: '100%', height: 38, marginTop: 6, padding: '0 10px', fontSize: 13, border: '1px solid var(--color-divider)', background: 'var(--color-bg)' }}
                >
                  {Object.entries(PRODUCTION_FORMAT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

          {error === null ? null : (
            <div style={{ marginTop: 14 }}>
              <Callout tone="danger" title="Not started" testId="new-search-error">
                {error}
              </Callout>
            </div>
          )}

          <button
            type="button"
            className="btn btn-primary"
            data-testid="analyse-locations"
            disabled={!ready || start.isPending}
            onClick={() => {
              setError(null);
              start.mutate();
            }}
            style={{ width: '100%', marginTop: 16 }}
          >
            {start.isPending ? 'Starting…' : mode === 'MANUAL' ? 'Open the production' : 'Analyse locations'}
          </button>

          {mode === 'MANUAL' ? null : (
            <p className="mm" style={{ fontSize: 11.5, margin: '10px 0 0', lineHeight: 1.6 }} data-testid="background-note">
              {/* What is TRUE: the run is a background job and the Script tab
                  shows its progress. The design said "we'll notify you when
                  it's ready" — this service sends no such notification, and a
                  promise of one is how somebody closes the tab and waits for an
                  email that never comes. */}
              Analysis runs in the background. You can leave this page — the production&rsquo;s
              Script tab shows how far it has got.
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}

const MODES: readonly { mode: Mode; title: string; hint: string }[] = [
  { mode: 'UPLOAD', title: 'Upload a file', hint: 'Screenplay PDF, DOCX or TXT' },
  { mode: 'PASTE', title: 'Paste script or brief', hint: 'Treatment, creative brief, shot list' },
  { mode: 'MANUAL', title: 'Add requirements manually', hint: 'No script needed' },
];

function FilePicker({ file, onPick }: { file: File | null; onPick: (f: File | null) => void }) {
  return (
    <div style={{ marginTop: 16 }}>
      <label
        style={{ display: 'inline-block', cursor: 'pointer' }}
        data-testid="search-upload-label"
      >
        <span className="ctl">{file === null ? 'Choose a file' : 'Replace'}</span>
        <input
          type="file"
          data-testid="search-upload"
          accept=".pdf,.docx,.txt,.fountain"
          onChange={(e) => {
            onPick(e.target.files?.[0] ?? null);
          }}
          style={{ display: 'none' }}
        />
      </label>

      {file === null ? null : (
        <div
          data-testid="search-file"
          style={{ marginTop: 12, padding: '14px 16px', borderLeft: '3px solid var(--ok, #2e7d32)', background: 'var(--color-surface)' }}
        >
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700 }}>{file.name}</p>
          <p className="mm" style={{ margin: '4px 0 0', fontSize: 12 }}>
            {/* SIZE ONLY. The design also showed "~118 pages detected", which
                nothing here can know before the file is parsed — a page count
                invented in the browser from a byte count would be wrong for
                every scanned PDF, which is exactly the case it would matter in. */}
            {(file.size / 1_000_000).toFixed(1)} MB
          </p>
        </div>
      )}
    </div>
  );
}

